import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wifi, WifiOff, CheckCircle, XCircle } from 'lucide-react'
import { UserStatus } from '../services/authService'

interface UserStatusModalProps {
  isOpen: boolean
  onClose: () => void
  userStatus: UserStatus | null
  onAutoImport?: () => void
  isImporting?: boolean
}

const UserStatusModal: React.FC<UserStatusModalProps> = ({ isOpen, onClose, userStatus, onAutoImport, isImporting = false }) => {
  if (!userStatus) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-900/95 backdrop-blur-lg border border-white/20 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Chart Data Status</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status Overview */}
            <div className="mb-6">
              <div className={`flex items-center space-x-3 p-4 rounded-lg ${
                userStatus.isOnline 
                  ? 'bg-green-500/20 border border-green-500/30' 
                  : 'bg-red-500/20 border border-red-500/30'
              }`}>
                {userStatus.isOnline ? (
                  <Wifi className="w-6 h-6 text-green-400" />
                ) : (
                  <WifiOff className="w-6 h-6 text-red-400" />
                )}
                <div>
                  <h3 className={`font-semibold ${
                    userStatus.isOnline ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {userStatus.isOnline ? 'Online' : 'Offline'}
                  </h3>
                  <p className="text-sm text-gray-400">{userStatus.message}</p>
                  {userStatus.astroAgentsLive > 0 && (
                    <p className="text-xs text-green-400 mt-1">
                      {userStatus.astroAgentsLive} Astro Agents available for analysis
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Chart Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Chart Data</h3>
              
              {/* Astro Agents Status */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-400">Astro Agents Status</span>
                  <span className="text-xs text-blue-400">
                    {userStatus.astroAgentsLive}/5 Live
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.round((userStatus.astroAgentsLive / 5) * 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
              
              {/* Live Astro Agents */}
              {userStatus.astroAgentsLive > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-400 mb-2">
                    Live Astro Agents ({userStatus.astroAgentsLive})
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 p-2 bg-green-500/10 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-white">
                        {userStatus.astroAgentsLive === 1 
                          ? '1 Astro Agent is live and ready to guide you!'
                          : `${userStatus.astroAgentsLive} Astro Agents are live and ready to guide you!`
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Astro Agents Status Message */}
              {userStatus.statusMessage && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-blue-400">{userStatus.statusMessage}</span>
                  </div>
                </div>
              )}

              {/* Total Astro Agents */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Total Astro Agents</span>
                  <span className="text-sm font-medium text-white">{userStatus.totalCharts}</span>
                </div>
              </div>

              {/* Retry Information */}
              {userStatus.retryAttempts && userStatus.retryAttempts > 0 && (
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Connection Attempts</span>
                    <span className="text-sm font-medium text-yellow-400">{userStatus.retryAttempts}/2</span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {!userStatus.isOnline && (
              <div className="mt-6 pt-4 border-t border-white/10 space-y-2">
                {onAutoImport && (
                  <button
                    onClick={onAutoImport}
                    disabled={isImporting}
                    className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-2 px-4 rounded-lg font-medium hover:from-green-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isImporting ? 'Activating Astro Agents...' : 'Activate Astro Agents'}
                  </button>
                )}
                <button
                  onClick={() => {
                    // Navigate to profile to generate charts
                    window.location.href = '/profile'
                  }}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                >
                  Activate Astro Agents Manually
                </button>
              </div>
            )}


          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default UserStatusModal 